import { AlertTriangle } from "lucide-react";
import { errorDetail, isSignedOut, readErrorReason } from "@/lib/api-error";
import { SignInAgain } from "@/components/SignInAgain";
import { RetryButton } from "@/components/RetryButton";
import { useIsUnattendedSurface } from "@/lib/unattended-session";

/**
 * A read that failed, said where the reader is already looking: what it was,
 * why, and a retry. Never reads as "no rows" (#3283, shared app-wide since
 * #3305, where Certificates, Waste and the alarm panel said "No data" on a
 * failed read). A 401 is the sign-in-again note instead (#3398).
 */
export function ReadFailed({
  what,
  error,
  onRetry,
  retrying = false,
  reason,
}: {
  /** Lower-case noun phrase: "certificates", "today's alarms". */
  what: string;
  error: unknown;
  /**
   * The line saying why, when the surface writes its own (QC vs TDS keeps
   * status codes and server text off the page, so no "Error details" line is
   * shown either). Defaults to readErrorReason.
   */
  reason?: string;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  // #3398: an ended session is not a fault, and Retry would fail the same way.
  // Except on an unattended screen (the wallboard): RequireAuth re-establishes
  // the session there silently and nobody is at the screen to follow a link,
  // so the read keeps its fault box and Retry until the session is back.
  const unattended = useIsUnattendedSurface();
  if (isSignedOut(error) && !unattended) return <SignInAgain />;
  // #4450 ERRCODE: the status is for support, so it sits folded under "Error
  // details" (QueryErrorBanner's pattern), never in the line staff read. A
  // surface that writes its own `reason` keeps codes off the page entirely.
  const detail = reason === undefined ? errorDetail(error) : null;
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-rose-500/30 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-500/5 dark:text-rose-300"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <strong className="font-medium">Couldn't load {what}.</strong>{" "}
        <span className="text-rose-700/80 dark:text-rose-300/80">{reason ?? readErrorReason(error)}</span>
        {detail ? (
          <details className="mt-0.5 text-xs text-rose-700/70 dark:text-rose-300/60">
            <summary className="cursor-pointer hover:text-rose-800 dark:hover:text-rose-200/80">Error details</summary>
            <span className="font-mono text-[10px]">{detail}</span>
          </details>
        ) : null}
      </div>
      {onRetry ? <RetryButton onRetry={onRetry} retrying={retrying} label={`Retry loading ${what}`} /> : null}
    </div>
  );
}
